import React, { useMemo } from "react";
import { Badge, Box, CheckboxGroup, CheckboxCard, HStack, Icon, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { DashboardCheckboxCardControl, DashboardInput } from "components/utils/FormControls";
import { MdRecordVoiceOver } from "react-icons/md";
import { FaPray } from "react-icons/fa";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { getAvatarHexColor, getAvatarTint } from "utils/avatar";
import { toNullIfEmpty } from "utils/formatters";
import { useDialogForm } from "utils/useDialogForm";

function buildNormasPiedadPayload(selectedIds, existingNormasPiedad) {
  const prevItems = (existingNormasPiedad?.items || []).map((it) => ({ norma: Number(it.norma), completada: it.completada ? true : false }));
  const prevMap = new Map(prevItems.map((it) => [it.norma, it.completada]));
  const uniq = Array.from(new Set((selectedIds || []).map((x) => Number(x)))).filter((n) => Number.isFinite(n));
  return { items: uniq.map((normaId) => ({ norma: normaId, completada: prevMap.get(normaId) ?? false })) };
}

function normaTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}

export const PrevioFormacionCard = ({ actividad, actividadId, patch, normaItems }) => {
  const dialog = useDialogForm({ temaCharla: "", selectedNormas: [] });

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;

  const temaCharlaTxt = actividad?.temaCharla?.trim() || "—";
  const savedNormas = useMemo(() => actividad?.normasPiedad?.items || [], [actividad]);
  const savedNormaIds = useMemo(() => savedNormas.map((it) => String(it.norma)), [savedNormas]);

  const normasSeleccionadas = useMemo(() => {
    const set = new Set(savedNormaIds);
    return (normaItems ?? []).filter((n) => set.has(String(n.id)));
  }, [savedNormaIds, normaItems]);

  function openDialog() {
    const existing = actividad?.normasPiedad?.items || [];
    dialog.openWith({ temaCharla: actividad?.temaCharla ?? "", selectedNormas: existing.map((it) => String(it.norma)) });
  }

  async function save() {
    const normasPiedadPayload = buildNormasPiedadPayload(dialog.form.selectedNormas, actividad?.normasPiedad);

    await patch.mutateAsync({
      actividadId,
      patch: {
        temaCharla: toNullIfEmpty(dialog.form.temaCharla),
        normasPiedad: normasPiedadPayload,
      },
    });

    dialog.close();
  }

  return (
    <>
      <DataCard title="Formación" variant="purple" headerIcon={<FaPray size="24px" />} onEdit={openDialog} editAriaLabel="Editar formación">
        <Box divideY="1px" divideColor="dashboard.border.muted">
          <HStack gap="2.5" align="start" pb="4">
            <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdRecordVoiceOver size="16px" /></Box>
            <Stack gap="0">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Tema de la Charla</Text>
              <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{temaCharlaTxt}</Text>
            </Stack>
          </HStack>

          <Box pt="4">
            <HStack justify="space-between" align="center" mb="3">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Normas de Piedad</Text>
              <Badge colorPalette={normasSeleccionadas.length > 0 ? "purple" : "gray"} variant="subtle" size="sm">
                {normasSeleccionadas.length > 0 ? `${normasSeleccionadas.length} asignadas` : "Sin normas"}
              </Badge>
            </HStack>

            {normasSeleccionadas.length === 0 ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Sin normas asignadas.</Text>
            ) : (
              <SimpleGrid minChildWidth="150px" gap="2.5">
                {normasSeleccionadas.map((n) => {
                  const tone = normaTone(n.id || n.label);
                  return (
                    <HStack key={n.id} gap="2.5" p="3" rounded="2xl" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted">
                      <Box bg={tone.solid} boxSize="9" display="grid" placeItems="center" rounded="full" flexShrink={0}>
                        <Icon fontSize="22px" color="white">
                          {n.icon}
                        </Icon>
                      </Box>
                      <Text fontSize="sm" fontWeight="800" color="dashboard.fg.default">{n.label}</Text>
                    </HStack>
                  );
                })}
              </SimpleGrid>
            )}
          </Box>
        </Box>
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Formación" errorText={patchError} saving={saving} onCancel={dialog.close} onSave={save}>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Tema de la Charla</Text>
          <DashboardInput value={dialog.form.temaCharla} onChange={(e) => dialog.setField("temaCharla", e.target.value)} placeholder="Ej. La sinceridad" />
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Normas de Piedad</Text>

          <CheckboxGroup
            name="normasPiedad"
            value={dialog.form.selectedNormas}
            onValueChange={(v) => {
              const next = Array.isArray(v) ? v : v?.value;
              dialog.setField("selectedNormas", next ?? []);
            }}
          >
            <SimpleGrid minChildWidth="125px" gap="2">
              {(normaItems ?? []).map((item) => {
                const tone = normaTone(item.id || item.label);
                return (
                  <CheckboxCard.Root unstyled align="center" key={item.id} value={String(item.id)}>
                    <CheckboxCard.HiddenInput />
                    <DashboardCheckboxCardControl accentColor={tone.solid} selectedVariant="outlined" selectedBg={tone.bg}>
                      <CheckboxCard.Content display="flex" flexDirection="column" alignItems="center" textAlign="center" w="full">
                        <Box bg={tone.solid} boxSize="11" display="grid" placeItems="center" rounded="full" mb="3" mx="auto">
                          <Icon fontSize="2xl" color="white">
                            {item.icon}
                          </Icon>
                        </Box>
                        <CheckboxCard.Label fontWeight="800">{item.label}</CheckboxCard.Label>
                        <CheckboxCard.Description color="dashboard.fg.muted">{item.description}</CheckboxCard.Description>
                      </CheckboxCard.Content>
                    </DashboardCheckboxCardControl>
                  </CheckboxCard.Root>
                );
              })}
            </SimpleGrid>
          </CheckboxGroup>
        </Box>
      </FormDialog>
    </>
  );
};
